import React, { createContext, useState, useEffect, useContext } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';

const NavigationBarPreferencesContext = createContext({
  logoURL: null,
  logoSize: 48,
  navBarStyle: 'classic',
  loading: true,
});

const LOGO_SIZE_MAP = {
  small: 32,
  default: 48,
  large: 72,
};

export function NavigationBarPreferencesProvider({ children }) {
  const [state, setState] = useState(() => {
    try {
      const cached = localStorage.getItem('navigationBarPreferences');
      if (cached) {
        const parsed = JSON.parse(cached);
        return {
          logoURL: parsed.logoURL || null,
          logoSize: parsed.logoSize || 48,
          navBarStyle: parsed.navBarStyle || 'classic',
          loading: true,
        };
      }
    } catch {}
    return { logoURL: null, logoSize: 48, navBarStyle: 'classic', loading: true };
  });

  useEffect(() => {
    const unsubscribe = onSnapshot(
      doc(db, 'navigation_bar_preferences', 'default'),
      (snapshot) => {
        let logoURL = null;
        let logoSize = 48;
        let navBarStyle = 'classic';

        if (snapshot.exists()) {
          const data = snapshot.data();
          logoURL = data?.logo_url || null;
          const size = data?.logo_size || 'default';
          logoSize = LOGO_SIZE_MAP[size] || 48;
          navBarStyle = data?.nav_bar_style || 'classic';

          // Dynamic favicon
          const faviconURL = data?.favicon_url || logoURL || '/favicon.png';
          if (faviconURL) {
            let link = document.querySelector('link[rel="icon"]');
            if (!link) {
              link = document.createElement('link');
              link.rel = 'icon';
              document.head.appendChild(link);
            }
            link.href = faviconURL;
          }
        }

        const prefs = { logoURL, logoSize, navBarStyle, loading: false };
        setState(prefs);

        try {
          localStorage.setItem('navigationBarPreferences', JSON.stringify({
            logoURL,
            logoSize,
            navBarStyle,
          }));
        } catch {}
      },
      (error) => {
        console.error('Error fetching navigation bar preferences:', error);
        setState((prev) => ({ ...prev, loading: false }));
      }
    );

    return () => unsubscribe();
  }, []);

  return (
    <NavigationBarPreferencesContext.Provider value={state}>
      {children}
    </NavigationBarPreferencesContext.Provider>
  );
}

export function useNavigationBarPreferences() {
  return useContext(NavigationBarPreferencesContext);
}
